import { PROGRAM_MAPPING } from './constants.js';

// Normalize center codes: strip Regional Hub suffix and map known aliases
const CENTER_CODE_MAP = { 'ABC': 'Alliance/CIAT' };

// Canonical CGIAR center directory: code → full name
export const CENTER_DIRECTORY = new Map([
  ['AfricaRice', 'Africa Rice Center'],
  ['Alliance/CIAT', 'Alliance of Bioversity International and CIAT'],
  ['CIFOR-ICRAF', 'Center for International Forestry Research and World Agroforestry'],
  ['CIMMYT', 'International Maize and Wheat Improvement Center'],
  ['CIP', 'International Potato Center'],
  ['ICARDA', 'International Center for Agricultural Research in the Dry Areas'],
  ['ICRISAT', 'International Crops Research Institute for the Semi-Arid Tropics'],
  ['IFPRI', 'International Food Policy Research Institute'],
  ['IITA', 'International Institute of Tropical Agriculture'],
  ['ILRI', 'International Livestock Research Institute'],
  ['IRRI', 'International Rice Research Institute'],
  ['IWMI', 'International Water Management Institute'],
  ['WorldFish', 'WorldFish Center'],
]);

// Reverse lookup: lowercase name/alias → canonical code
const CENTER_NAME_LOOKUP = new Map();
// Populate from directory (full names)
CENTER_DIRECTORY.forEach((fullName, code) => {
  CENTER_NAME_LOOKUP.set(fullName.toLowerCase(), code);
  CENTER_NAME_LOOKUP.set(code.toLowerCase(), code);
});
// Known aliases
const CENTER_ALIASES = {
  'alliance': 'Alliance/CIAT', 'abc': 'Alliance/CIAT',
  'ciat': 'Alliance/CIAT', 'bioversity': 'Alliance/CIAT', 'ciat-bioversity': 'Alliance/CIAT',
  'cifor': 'CIFOR-ICRAF', 'icraf': 'CIFOR-ICRAF',
  'africarice': 'AfricaRice', 'africa rice': 'AfricaRice', 'africa rice center': 'AfricaRice',
  'worldfish': 'WorldFish', 'worldfish center': 'WorldFish',
};
Object.entries(CENTER_ALIASES).forEach(([alias, code]) => {
  CENTER_NAME_LOOKUP.set(alias.toLowerCase(), code);
});

/**
 * Normalize a string to a canonical center code.
 * Tries: direct code match → normalizeCenterCode → reverse name lookup → parenthesized acronym.
 * Returns null if not a recognized CGIAR center.
 */
export function normalizeToCenter(str) {
  if (!str) return null;
  const trimmed = str.trim();
  // 1. Direct match: str is a known code
  if (CENTER_DIRECTORY.has(trimmed)) return trimmed;
  // 2. Alias match via normalizeCenterCode (strips RH suffix, maps ABC→Alliance/CIAT)
  const normalized = normalizeCenterCode(trimmed);
  if (CENTER_DIRECTORY.has(normalized)) return normalized;
  // 3. Reverse name lookup (lowercase)
  const lower = trimmed.toLowerCase();
  if (CENTER_NAME_LOOKUP.has(lower)) return CENTER_NAME_LOOKUP.get(lower);
  // 4. Parenthesized acronym: extract (CODE) from end of string
  const parenMatch = trimmed.match(/\(([^)]+)\)\s*$/);
  if (parenMatch) {
    const inner = parenMatch[1].trim();
    if (CENTER_DIRECTORY.has(inner)) return inner;
    const innerNorm = normalizeCenterCode(inner);
    if (CENTER_DIRECTORY.has(innerNorm)) return innerNorm;
    const innerLower = inner.toLowerCase();
    if (CENTER_NAME_LOOKUP.has(innerLower)) return CENTER_NAME_LOOKUP.get(innerLower);
  }
  // 5. Try substring match on the name part before " - " (e.g., "CIMMYT - International...")
  const dashIdx = trimmed.indexOf(' - ');
  if (dashIdx > -1) {
    const prefix = trimmed.substring(0, dashIdx).trim();
    const prefixNorm = normalizeCenterCode(prefix);
    if (CENTER_DIRECTORY.has(prefixNorm)) return prefixNorm;
    if (CENTER_NAME_LOOKUP.has(prefix.toLowerCase())) return CENTER_NAME_LOOKUP.get(prefix.toLowerCase());
  }
  return null;
}

/**
 * Extract all canonical center codes from a single result (lead + contributing).
 * Returns a deduplicated array of canonical codes.
 */
export function parseResultCenterCodes(result) {
  const codes = new Set();

  // Lead center
  const leadCode = normalizeToCenter(result.leadCenter);
  if (leadCode) codes.add(leadCode);

  // Contributing centers — pipeline path
  if (result.centersParsed && result.centersParsed.length > 0) {
    result.centersParsed.forEach(({ code: rawCode }) => {
      const c = normalizeCenterCode(rawCode);
      const canonical = normalizeToCenter(c);
      if (canonical) codes.add(canonical);
    });
  } else if (result.contributingCenters) {
    // Fallback: parse from raw text
    const entries = result.contributingCenters.split(/\s*\/n\s*/).map(s => s.trim()).filter(Boolean);
    entries.forEach(entry => {
      const cleaned = entry.replace(/^\(|\)$/g, '').trim();
      if (!cleaned) return;
      const namePart = cleaned.replace(/^Primary:\s*/i, '').trim();
      const canonical = normalizeToCenter(namePart);
      if (canonical) codes.add(canonical);
    });
  }

  return [...codes];
}

// CGIAR center acronyms to exclude from external partner charts.
// These should only appear in ContributingCentersChart (which uses parseContributingCenters).
const CGIAR_CENTER_ACRONYMS = new Set([
  'AfricaRice',
  'Alliance', 'ABC',
  'CIAT', 'Bioversity', 'CIAT-BIOVERSITY',
  'CIFOR', 'ICRAF', 'CIFOR-ICRAF',
  'CIMMYT',
  'CIP',
  'ICARDA',
  'ICRISAT',
  'IFPRI',
  'IITA',
  'ILRI',
  'IRRI',
  'IWMI',
  'WorldFish',
]);

export function isCGIARCenter(acronym) {
  if (!acronym) return false;
  if (CGIAR_CENTER_ACRONYMS.has(acronym)) return true;
  if (acronym.startsWith('CGIAR')) return true;
  return false;
}

export function normalizeCenterCode(code) {
  if (!code) return code;
  const stripped = code.replace(/\s+RH$/i, '').trim();
  return CENTER_CODE_MAP[stripped] || stripped;
}

// Combine raw partner text from both columns, filtering out sentinel values.
// The `||` pattern (partners || partnersKP) silently drops partnersKP when
// partners is "Not Applicable" (truthy). This merges both columns instead.
export function combineRawPartnerTexts(partners, partnersKP) {
  const skip = ['Not Applicable', 'Not applicable for KP', ''];
  const texts = [partners, partnersKP].filter(t => t && !skip.includes(t));
  return texts.join('\n');
}

// Parse partner details for network/top partner analysis.
// Uses pre-parsed partnersParsed/partnersKPParsed arrays if available (pipeline path),
// otherwise falls back to parsing raw text (legacy/manual upload path).
export function parsePartnerDetails(results) {
  const partners = new Map();

  results.forEach(result => {
    // Pipeline path: use pre-parsed partner arrays
    const preParsed = [
      ...(result.partnersParsed || []),
      ...(result.partnersKPParsed || []),
    ];

    if (preParsed.length > 0) {
      preParsed.forEach(({ acronym, fullName, deliveryTypes }) => {
        if (!acronym || isCGIARCenter(acronym)) return;
        const dedupKey = fullName || acronym;
        if (!partners.has(dedupKey)) {
          partners.set(dedupKey, {
            acronym,
            fullName: fullName || acronym,
            deliveryTypes: new Set(deliveryTypes || []),
            resultCount: 0,
          });
        }
        const p = partners.get(dedupKey);
        p.resultCount++;
        (deliveryTypes || []).forEach(dt => p.deliveryTypes.add(dt));
      });
      return;
    }

    // Fallback: parse from raw text (merge both columns)
    const partnerText = combineRawPartnerTexts(result.partners, result.partnersKP);
    if (!partnerText) return;

    const entries = partnerText.split('•').map(s => s.trim()).filter(Boolean);

    entries.forEach(entry => {
      const semiIdx = entry.indexOf(';');
      const namePart = semiIdx > -1 ? entry.substring(0, semiIdx).trim() : entry.trim();

      let deliveryTypes = [];
      const dtMatch = entry.match(/Delivery type\(s\):\s*(.+)/i);
      if (dtMatch) {
        deliveryTypes = dtMatch[1].split(',').map(s => s.trim()).filter(Boolean);
      }

      const dashIdx = namePart.indexOf(' - ');
      const acronym = dashIdx > -1 ? namePart.substring(0, dashIdx).trim() : namePart;
      const fullName = dashIdx > -1 ? namePart.substring(dashIdx + 3).trim() : namePart;

      if (!acronym || isCGIARCenter(acronym)) return;
      const dedupKey = fullName || acronym;

      if (!partners.has(dedupKey)) {
        partners.set(dedupKey, {
          acronym,
          fullName,
          deliveryTypes: new Set(deliveryTypes),
          resultCount: 0,
        });
      }
      const p = partners.get(dedupKey);
      p.resultCount++;
      deliveryTypes.forEach(dt => p.deliveryTypes.add(dt));
    });
  });

  // Convert Sets to arrays for serialization
  partners.forEach(p => { p.deliveryTypes = [...p.deliveryTypes]; });
  return partners;
}

// Calculate portfolio linkages for accelerator mode
export function calculatePortfolioLinkages(results, selectedProgram = null) {
  const programLinkages = {};
  const crossLinks = new Map();

  results.forEach(result => {
    const inits = result.contributingInitiatives || [];
    // Also include the primary initiative
    const allInits = result.initiative ? [result.initiative, ...inits] : inits;
    const uniqueInits = [...new Set(allInits)];

    uniqueInits.forEach(init => {
      if (selectedProgram && init === selectedProgram) return;
      const name = PROGRAM_MAPPING[init] || init;
      if (!programLinkages[name]) programLinkages[name] = { resultCount: 0 };
      programLinkages[name].resultCount++;
    });

    const filteredInits = selectedProgram ? uniqueInits.filter(i => i !== selectedProgram) : uniqueInits;
    for (let i = 0; i < filteredInits.length; i++) {
      for (let j = i + 1; j < filteredInits.length; j++) {
        const key = [filteredInits[i], filteredInits[j]].sort().join('|');
        crossLinks.set(key, (crossLinks.get(key) || 0) + 1);
      }
    }
  });

  return {
    programLinkages,
    crossLinks: [...crossLinks.entries()].map(([key, count]) => {
      const [a, b] = key.split('|');
      return { source: PROGRAM_MAPPING[a] || a, target: PROGRAM_MAPPING[b] || b, count };
    }),
    totalPrograms: Object.keys(programLinkages).length,
    jointResults: [...crossLinks.values()].reduce((s, v) => s + v, 0),
  };
}

// Parse contributing centers from results.
// Uses pre-parsed centersParsed arrays if available (pipeline path).
export function parseContributingCenters(results) {
  const centers = new Map();

  results.forEach(result => {
    // Pipeline path: use pre-parsed center arrays
    if (result.centersParsed && result.centersParsed.length > 0) {
      result.centersParsed.forEach(({ code: rawCode, fullName, isPrimary }) => {
        const code = normalizeCenterCode(rawCode);
        if (!code) return;
        if (!centers.has(code)) {
          centers.set(code, { code, fullName: fullName || code, isPrimary: false, resultCount: 0 });
        }
        const c = centers.get(code);
        c.resultCount++;
        if (isPrimary) c.isPrimary = true;
      });
      return;
    }

    // Fallback: parse from raw text
    const text = result.contributingCenters;
    if (!text) return;

    const entries = text.split(/\s*\/n\s*/).map(s => s.trim()).filter(Boolean);

    entries.forEach(entry => {
      const cleaned = entry.replace(/^\(|\)$/g, '').trim();
      if (!cleaned) return;

      const isPrimary = /^Primary:/i.test(cleaned);
      const namePart = cleaned.replace(/^Primary:\s*/i, '').trim();

      const dashIdx = namePart.indexOf(' - ');
      const rawCode = dashIdx > -1 ? namePart.substring(0, dashIdx).trim() : namePart;
      const fullName = dashIdx > -1 ? namePart.substring(dashIdx + 3).trim() : namePart;
      const code = normalizeCenterCode(rawCode);

      if (!code) return;

      if (!centers.has(code)) {
        centers.set(code, { code, fullName, isPrimary: false, resultCount: 0 });
      }
      const c = centers.get(code);
      c.resultCount++;
      if (isPrimary) c.isPrimary = true;
    });
  });

  return centers;
}

// Parse funders from non-pooled project data (W3Bilateral results only).
// Uses pre-parsed fundersParsed arrays if available (pipeline path).
export function parseFunders(results) {
  const funders = new Map();

  results.forEach(result => {
    // Pipeline path: use pre-parsed funder arrays
    if (result.fundersParsed && result.fundersParsed.length > 0) {
      result.fundersParsed.forEach(({ funderName, grantTitle }) => {
        if (!funderName) return;
        if (!funders.has(funderName)) {
          funders.set(funderName, { funderName, grantCount: 0, resultCount: 0, grants: new Set() });
        }
        const f = funders.get(funderName);
        f.resultCount++;
        if (grantTitle) f.grants.add(grantTitle);
      });
      return;
    }

    // Fallback: parse from raw text
    const text = result.nonPooledProjects;
    if (!text) return;

    const blocks = text.split(/[\r\n]+/).map(s => s.trim()).filter(Boolean);

    blocks.forEach(block => {
      const funderMatch = block.match(/Funder name:\s*([^,)]+)/);
      const grantMatch = block.match(/Grant title:\s*(.+?)(?=,\s*(?:Center Grant ID|Lead[\s/]*Contract Center)[:\s]|\)\s*$)/);
      if (!funderMatch) return;

      const funderName = funderMatch[1].trim();
      const grantTitle = grantMatch ? grantMatch[1].trim() : '';
      if (!funderName) return;

      if (!funders.has(funderName)) {
        funders.set(funderName, { funderName, grantCount: 0, resultCount: 0, grants: new Set() });
      }
      const f = funders.get(funderName);
      f.resultCount++;
      if (grantTitle) f.grants.add(grantTitle);
    });
  });

  // Convert Sets to arrays and compute grantCount
  funders.forEach(f => {
    f.grants = [...f.grants];
    f.grantCount = f.grants.length;
  });

  return funders;
}

// Build partner-to-program edge list for network visualization.
// Uses pre-parsed partner arrays if available (pipeline path).
export function buildPartnerProgramEdges(results, selectedProgram) {
  const edgeMap = new Map(); // "acronym|SP01" → count
  const partnerAgg = new Map(); // acronym → { acronym, fullName, deliveryTypes Set, totalResults }
  const programAgg = new Map(); // code → { code, name, totalResults }

  // When a specific program is selected, attribute all results to it
  const forcedProgram = selectedProgram && selectedProgram !== 'all' ? selectedProgram : null;

  results.forEach(result => {
    const program = forcedProgram || result.initiative;
    if (!program) return;

    // Get partner entries — pipeline path or fallback
    const preParsed = [
      ...(result.partnersParsed || []),
      ...(result.partnersKPParsed || []),
    ];

    let partnerEntries;
    if (preParsed.length > 0) {
      partnerEntries = preParsed;
    } else {
      // Fallback: parse from raw text (merge both columns)
      const partnerText = combineRawPartnerTexts(result.partners, result.partnersKP);
      if (!partnerText) return;

      partnerEntries = partnerText.split('•').map(s => s.trim()).filter(Boolean).map(entry => {
        const semiIdx = entry.indexOf(';');
        const namePart = semiIdx > -1 ? entry.substring(0, semiIdx).trim() : entry.trim();
        let deliveryTypes = [];
        const dtMatch = entry.match(/Delivery type\(s\):\s*(.+)/i);
        if (dtMatch) {
          deliveryTypes = dtMatch[1].split(',').map(s => s.trim()).filter(Boolean);
        }
        const dashIdx = namePart.indexOf(' - ');
        const acronym = dashIdx > -1 ? namePart.substring(0, dashIdx).trim() : namePart;
        const fullName = dashIdx > -1 ? namePart.substring(dashIdx + 3).trim() : namePart;
        return { acronym, fullName, deliveryTypes };
      }).filter(e => e.acronym);
    }

    if (partnerEntries.length === 0) return;

    // Track program node
    if (!programAgg.has(program)) {
      programAgg.set(program, { code: program, name: PROGRAM_MAPPING[program] || program, totalResults: 0 });
    }
    programAgg.get(program).totalResults++;

    partnerEntries.forEach(({ acronym, fullName, deliveryTypes }) => {
      if (!acronym || isCGIARCenter(acronym)) return;
      const dedupKey = fullName || acronym;

      // Track partner node
      if (!partnerAgg.has(dedupKey)) {
        partnerAgg.set(dedupKey, { acronym, fullName: fullName || acronym, deliveryTypes: new Set(deliveryTypes || []), totalResults: 0 });
      }
      const p = partnerAgg.get(dedupKey);
      p.totalResults++;
      (deliveryTypes || []).forEach(dt => p.deliveryTypes.add(dt));

      // Track edge
      const edgeKey = `${dedupKey}|${program}`;
      edgeMap.set(edgeKey, (edgeMap.get(edgeKey) || 0) + 1);
    });
  });

  // Convert partner deliveryTypes Sets to arrays, sort by totalResults, take top 20
  const partnerNodes = [...partnerAgg.values()]
    .map(p => ({ ...p, deliveryTypes: [...p.deliveryTypes] }))
    .sort((a, b) => b.totalResults - a.totalResults)
    .slice(0, 20);

  const topPartnerSet = new Set(partnerNodes.map(p => p.fullName || p.acronym));
  const programNodes = [...programAgg.values()].sort((a, b) => b.totalResults - a.totalResults);

  // Build edges — only for top 20 partners, only count >= 2
  const edges = [...edgeMap.entries()]
    .map(([key, count]) => {
      const [partner, program] = key.split('|');
      return { partner, program, programName: PROGRAM_MAPPING[program] || program, count };
    })
    .filter(e => topPartnerSet.has(e.partner) && e.count >= 2)
    .sort((a, b) => b.count - a.count);

  return { edges, partnerNodes, programNodes };
}

// Build cross-program collaboration matrix from contributing initiatives
export function buildCrossProgramMatrix(results) {
  const pairCounts = new Map();
  const programSet = new Set();

  results.forEach(result => {
    const inits = result.contributingInitiatives || [];
    const allInits = result.initiative ? [result.initiative, ...inits] : inits;
    const unique = [...new Set(allInits)].filter(Boolean);

    unique.forEach(p => programSet.add(p));

    // For each unique pair, increment count
    for (let i = 0; i < unique.length; i++) {
      for (let j = i + 1; j < unique.length; j++) {
        const key = [unique[i], unique[j]].sort().join('|');
        pairCounts.set(key, (pairCounts.get(key) || 0) + 1);
      }
    }
  });

  const pairs = [...pairCounts.entries()]
    .map(([key, count]) => {
      const [source, target] = key.split('|');
      return {
        source,
        target,
        sourceName: PROGRAM_MAPPING[source] || source,
        targetName: PROGRAM_MAPPING[target] || target,
        count,
      };
    })
    .sort((a, b) => b.count - a.count);

  return { pairs, programs: [...programSet].sort() };
}
